import React, { Suspense, lazy, Component } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AppLayout } from '../layouts/AppLayout';
import { PageTransitionWrapper } from '../../shared/ui/PageTransitionWrapper';
import { ProtectedRoute } from './ProtectedRoute';
import { LoadingScreen } from '../../shared/ui/LoadingScreen';
import { Login } from '../../features/auth/pages/Login';
// Lazy-loaded feature pages. Named exports are mapped to default for lazy.
const lazyNamed = <T extends Record<string, unknown>,>(loader: () => Promise<T>, name: keyof T) => lazy(() => loader().then(m => ({
  default: m[name] as ComponentType<unknown>
})));
// analytics
const Overview = lazyNamed(() => import('../../features/analytics'), 'Overview');
const CustomMetrics = lazyNamed(() => import('../../features/analytics'), 'CustomMetrics');
const Nadi = lazyNamed(() => import('../../features/analytics'), 'Nadi');
const Reports = lazyNamed(() => import('../../features/analytics'), 'Reports');
const CardMonitoringPrograms = lazyNamed(() => import('../../features/analytics'), 'CardMonitoringPrograms');
const VampDetail = lazyNamed(() => import('../../features/analytics'), 'VampDetail');
const PaymentAnalytics = lazyNamed(() => import('../../features/analytics'), 'PaymentAnalytics');
// notifications
const Notifications = lazyNamed(() => import('../../features/notifications'), 'Notifications');
// payments
const PaymentRequests = lazyNamed(() => import('../../features/payments'), 'PaymentRequests');
const BulkPayments = lazyNamed(() => import('../../features/payments'), 'BulkPayments');
const BulkPaymentDetail = lazyNamed(() => import('../../features/payments'), 'BulkPaymentDetail');
const VirtualTerminal = lazyNamed(() => import('../../features/payments'), 'VirtualTerminal');
const QRCodes = lazyNamed(() => import('../../features/payments'), 'QRCodes');
// settings
const Settings = lazyNamed(() => import('../../features/settings'), 'Settings');
const UpdatePayoutAccount = lazyNamed(() => import('../../features/settings'), 'UpdatePayoutAccount');
// balance
const PayoutSettings = lazyNamed(() => import('../../features/balance'), 'PayoutSettings');
const BalanceSummary = lazyNamed(() => import('../../features/balance'), 'BalanceSummary');
const BalanceActivity = lazyNamed(() => import('../../features/balance'), 'BalanceActivity');
const BalanceActivityDetail = lazyNamed(() => import('../../features/balance'), 'BalanceActivityDetail');
const Payouts = lazyNamed(() => import('../../features/balance'), 'Payouts');
const PayoutDetail = lazyNamed(() => import('../../features/balance'), 'PayoutDetail');
// terminals
const PhysicalTerminals = lazyNamed(() => import('../../features/terminals'), 'PhysicalTerminals');
const SoftPosTerminals = lazyNamed(() => import('../../features/terminals'), 'SoftPosTerminals');
const Connect = lazyNamed(() => import('../../features/terminals'), 'Connect');
// developers
const WebhookEndpoints = lazyNamed(() => import('../../features/developers'), 'WebhookEndpoints');
const WebhookLogs = lazyNamed(() => import('../../features/developers'), 'WebhookLogs');
// tutorials
const Tutorials = lazyNamed(() => import('../../features/tutorials'), 'Tutorials');
const TutorialDetail = lazyNamed(() => import('../../features/tutorials'), 'TutorialDetail');
// shops (orders, shops, campaigns, sales, inventory)
const Orders = lazyNamed(() => import('../../features/shops'), 'Orders');
const OrderDetail = lazyNamed(() => import('../../features/shops'), 'OrderDetail');
const Shops = lazyNamed(() => import('../../features/shops'), 'Shops');
const ShopDetails = lazyNamed(() => import('../../features/shops'), 'ShopDetails');
const Campaigns = lazyNamed(() => import('../../features/shops'), 'Campaigns');
const CampaignDetails = lazyNamed(() => import('../../features/shops'), 'CampaignDetails');
const CampaignForm = lazyNamed(() => import('../../features/shops'), 'CampaignForm');
const Inventory = lazyNamed(() => import('../../features/shops'), 'Inventory');
const Sales = lazyNamed(() => import('../../features/shops'), 'Sales');
const SaleDetail = lazyNamed(() => import('../../features/shops'), 'SaleDetail');
const ShopSettings = lazyNamed(() => import('../../features/shops'), 'ShopSettings');
// shifts
const ShiftsList = lazyNamed(() => import('../../features/shifts'), 'ShiftsList');
const ShiftDetail = lazyNamed(() => import('../../features/shifts'), 'ShiftDetail');
const ProductDetail = lazyNamed(() => import('../../features/shops'), 'ProductDetail');
const ProductEdit = lazyNamed(() => import('../../features/shops'), 'ProductEdit');
// rooms
const PropertyInformation = lazyNamed(() => import('../../features/rooms'), 'PropertyInformation');
const Rooms = lazyNamed(() => import('../../features/rooms'), 'Rooms');
const AddRoom = lazyNamed(() => import('../../features/rooms'), 'AddRoom');
const EditRoom = lazyNamed(() => import('../../features/rooms'), 'EditRoom');
const RoomDetail = lazyNamed(() => import('../../features/rooms'), 'RoomDetail');
const Rates = lazyNamed(() => import('../../features/rooms'), 'Rates');
const Reservations = lazyNamed(() => import('../../features/rooms'), 'Reservations');
const ReservationDetail = lazyNamed(() => import('../../features/rooms'), 'ReservationDetail');
// invoices
const Invoices = lazyNamed(() => import('../../features/invoices'), 'Invoices');
const InvoiceDetail = lazyNamed(() => import('../../features/invoices'), 'InvoiceDetail');
const InvoiceForm = lazyNamed(() => import('../../features/invoices'), 'InvoiceForm');
// customers
const Customers = lazyNamed(() => import('../../features/customers'), 'Customers');
const CustomerDetail = lazyNamed(() => import('../../features/customers'), 'CustomerDetail');
const CustomerForm = lazyNamed(() => import('../../features/customers'), 'CustomerForm');
// suppliers
const Suppliers = lazyNamed(() => import('../../features/suppliers'), 'Suppliers');
const SupplierDetail = lazyNamed(() => import('../../features/suppliers'), 'SupplierDetail');
const SupplierForm = lazyNamed(() => import('../../features/suppliers'), 'SupplierForm');
const SupplierSelfService = lazyNamed(() => import('../../features/suppliers'), 'SupplierSelfService');
const BulkSupplierUpload = lazyNamed(() => import('../../features/suppliers'), 'BulkSupplierUpload');
// reconciliation
const Reconciliation = lazyNamed(() => import('../../features/reconciliation'), 'Reconciliation');
// bills
const Bills = lazyNamed(() => import('../../features/bills'), 'Bills');
const BillDetail = lazyNamed(() => import('../../features/bills'), 'BillDetail');
const BillForm = lazyNamed(() => import('../../features/bills'), 'BillForm');
const BillApprovalWizard = lazyNamed(() => import('../../features/bills'), 'BillApprovalWizard');
const BillFinancingWizard = lazyNamed(() => import('../../features/bills'), 'BillFinancingWizard');
const BulkBillUpload = lazyNamed(() => import('../../features/bills'), 'BulkBillUpload');
// disputes
const Disputes = lazyNamed(() => import('../../features/disputes'), 'Disputes');
const DisputeDetail = lazyNamed(() => import('../../features/disputes'), 'DisputeDetail');
const ChallengeDispute = lazyNamed(() => import('../../features/disputes'), 'ChallengeDispute');
// refunds
const Refunds = lazyNamed(() => import('../../features/refunds'), 'Refunds');
const RefundDetail = lazyNamed(() => import('../../features/refunds'), 'RefundDetail');
// system
const ActivityLogs = lazyNamed(() => import('../../features/system'), 'ActivityLogs');
const Requests = lazyNamed(() => import('../../features/system'), 'Requests');
const RequestDetail = lazyNamed(() => import('../../features/system'), 'RequestDetail');
export function AppRouter() {
  return <BrowserRouter data-id="element-237">
      <Suspense fallback={<LoadingScreen data-id="element-239" />} data-id="element-238">
        <Routes data-id="element-240">
          <Route path="/" element={<Login data-id="element-242" />} data-id="element-241" />
          <Route path="/supplier-onboarding/:id" element={<SupplierSelfService data-id="element-244" />} data-id="element-243" />

          <Route path="/overview" element={<ProtectedRoute data-id="element-246">
                <AppLayout data-id="element-247">
                  <PageTransitionWrapper data-id="element-248">
                    <Overview data-id="element-249" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-245" />
          <Route path="/notifications" element={<ProtectedRoute data-id="element-251">
                <AppLayout data-id="element-252">
                  <PageTransitionWrapper data-id="element-253">
                    <Notifications data-id="element-254" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-250" />
          <Route path="/payment-requests" element={<ProtectedRoute data-id="element-256">
                <AppLayout data-id="element-257">
                  <PageTransitionWrapper data-id="element-258">
                    <PaymentRequests data-id="element-259" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-255" />
          <Route path="/qr-codes" element={<ProtectedRoute data-id="element-261">
                <AppLayout data-id="element-262">
                  <PageTransitionWrapper data-id="element-263">
                    <QRCodes data-id="element-264" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-260" />
          <Route path="/bulk-payments" element={<ProtectedRoute data-id="element-266">
                <AppLayout data-id="element-267">
                  <PageTransitionWrapper data-id="element-268">
                    <BulkPayments data-id="element-269" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-265" />
          <Route path="/bulk-payment-detail/:id" element={<ProtectedRoute data-id="element-271">
                <AppLayout data-id="element-272">
                  <PageTransitionWrapper data-id="element-273">
                    <BulkPaymentDetail data-id="element-274" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-270" />
          <Route path="/settings" element={<ProtectedRoute data-id="element-276">
                <AppLayout data-id="element-277">
                  <PageTransitionWrapper data-id="element-278">
                    <Settings data-id="element-279" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-275" />
          <Route path="/settings/payouts" element={<ProtectedRoute data-id="element-281">
                <AppLayout data-id="element-282">
                  <PageTransitionWrapper data-id="element-283">
                    <PayoutSettings data-id="element-284" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-280" />
          <Route path="/settings/payout-account/edit" element={<ProtectedRoute data-id="element-286">
                <AppLayout data-id="element-287">
                  <PageTransitionWrapper data-id="element-288">
                    <UpdatePayoutAccount data-id="element-289" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-285" />
          <Route path="/virtual-terminal" element={<ProtectedRoute data-id="element-291">
                <AppLayout data-id="element-292">
                  <PageTransitionWrapper data-id="element-293">
                    <VirtualTerminal data-id="element-294" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-290" />
          <Route path="/physical-terminals" element={<ProtectedRoute data-id="element-296">
                <AppLayout data-id="element-297">
                  <PageTransitionWrapper data-id="element-298">
                    <PhysicalTerminals data-id="element-299" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-295" />
          <Route path="/softpos-terminals" element={<ProtectedRoute data-id="element-301">
                <AppLayout data-id="element-302">
                  <PageTransitionWrapper data-id="element-303">
                    <SoftPosTerminals data-id="element-304" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-300" />
          <Route path="/connect" element={<ProtectedRoute data-id="element-306">
                <AppLayout data-id="element-307">
                  <PageTransitionWrapper data-id="element-308">
                    <Connect data-id="element-309" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-305" />
          <Route path="/webhooks/endpoints" element={<ProtectedRoute data-id="element-311">
                <AppLayout data-id="element-312">
                  <PageTransitionWrapper data-id="element-313">
                    <WebhookEndpoints data-id="element-314" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-310" />
          <Route path="/webhooks/logs" element={<ProtectedRoute data-id="element-316">
                <AppLayout data-id="element-317">
                  <PageTransitionWrapper data-id="element-318">
                    <WebhookLogs data-id="element-319" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-315" />
          <Route path="/tutorials" element={<ProtectedRoute data-id="element-321">
                <AppLayout data-id="element-322">
                  <PageTransitionWrapper data-id="element-323">
                    <Tutorials data-id="element-324" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-320" />
          <Route path="/tutorial/:id" element={<ProtectedRoute data-id="element-326">
                <AppLayout data-id="element-327">
                  <PageTransitionWrapper data-id="element-328">
                    <TutorialDetail data-id="element-329" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-325" />
          <Route path="/orders" element={<ProtectedRoute data-id="element-331">
                <AppLayout data-id="element-332">
                  <PageTransitionWrapper data-id="element-333">
                    <Orders data-id="element-334" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-330" />
          <Route path="/order-detail/:id" element={<ProtectedRoute data-id="element-336">
                <AppLayout data-id="element-337">
                  <PageTransitionWrapper data-id="element-338">
                    <OrderDetail data-id="element-339" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-335" />
          <Route path="/shops" element={<ProtectedRoute data-id="element-341">
                <AppLayout data-id="element-342">
                  <PageTransitionWrapper data-id="element-343">
                    <Shops data-id="element-344" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-340" />
          <Route path="/shifts" element={<ProtectedRoute data-id="element-346">
                <AppLayout data-id="element-347">
                  <PageTransitionWrapper data-id="element-348">
                    <ShiftsList data-id="element-349" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-345" />
          <Route path="/shifts/:id" element={<ProtectedRoute data-id="element-351">
                <AppLayout data-id="element-352">
                  <PageTransitionWrapper data-id="element-353">
                    <ShiftDetail data-id="element-354" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-350" />
          <Route path="/shop-details" element={<ProtectedRoute data-id="element-356">
                <AppLayout data-id="element-357">
                  <PageTransitionWrapper data-id="element-358">
                    <ShopDetails data-id="element-359" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-355" />
          <Route path="/shop-settings/:shopId" element={<ProtectedRoute data-id="element-361">
                <AppLayout data-id="element-362">
                  <PageTransitionWrapper data-id="element-363">
                    <ShopSettings data-id="element-364" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-360" />
          <Route path="/shop-settings" element={<ProtectedRoute data-id="element-366">
                <AppLayout data-id="element-367">
                  <PageTransitionWrapper data-id="element-368">
                    <ShopSettings data-id="element-369" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-365" />
          <Route path="/product-detail/:productId" element={<ProtectedRoute data-id="element-371">
                <AppLayout data-id="element-372">
                  <PageTransitionWrapper data-id="element-373">
                    <ProductDetail data-id="element-374" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-370" />
          <Route path="/product-edit/:productId" element={<ProtectedRoute data-id="element-376">
                <AppLayout data-id="element-377">
                  <PageTransitionWrapper data-id="element-378">
                    <ProductEdit data-id="element-379" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-375" />
          <Route path="/campaigns" element={<ProtectedRoute data-id="element-381">
                <AppLayout data-id="element-382">
                  <PageTransitionWrapper data-id="element-383">
                    <Campaigns data-id="element-384" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-380" />
          <Route path="/campaign-details" element={<ProtectedRoute data-id="element-386">
                <AppLayout data-id="element-387">
                  <PageTransitionWrapper data-id="element-388">
                    <CampaignDetails data-id="element-389" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-385" />
          <Route path="/campaign-form" element={<ProtectedRoute data-id="element-391">
                <AppLayout data-id="element-392">
                  <PageTransitionWrapper data-id="element-393">
                    <CampaignForm data-id="element-394" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-390" />
          <Route path="/property-information" element={<ProtectedRoute data-id="element-396">
                <AppLayout data-id="element-397">
                  <PageTransitionWrapper data-id="element-398">
                    <PropertyInformation data-id="element-399" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-395" />
          <Route path="/rooms" element={<ProtectedRoute data-id="element-401">
                <AppLayout data-id="element-402">
                  <PageTransitionWrapper data-id="element-403">
                    <Rooms data-id="element-404" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-400" />
          <Route path="/add-room" element={<ProtectedRoute data-id="element-406">
                <AppLayout data-id="element-407">
                  <PageTransitionWrapper data-id="element-408">
                    <AddRoom data-id="element-409" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-405" />
          <Route path="/edit-room" element={<ProtectedRoute data-id="element-411">
                <AppLayout data-id="element-412">
                  <PageTransitionWrapper data-id="element-413">
                    <EditRoom data-id="element-414" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-410" />
          <Route path="/room-detail" element={<ProtectedRoute data-id="element-416">
                <AppLayout data-id="element-417">
                  <PageTransitionWrapper data-id="element-418">
                    <RoomDetail data-id="element-419" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-415" />
          <Route path="/inventory" element={<ProtectedRoute data-id="element-421">
                <AppLayout data-id="element-422">
                  <PageTransitionWrapper data-id="element-423">
                    <Inventory data-id="element-424" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-420" />
          <Route path="/rates" element={<ProtectedRoute data-id="element-426">
                <AppLayout data-id="element-427">
                  <PageTransitionWrapper data-id="element-428">
                    <Rates data-id="element-429" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-425" />
          <Route path="/reservations" element={<ProtectedRoute data-id="element-431">
                <AppLayout data-id="element-432">
                  <PageTransitionWrapper data-id="element-433">
                    <Reservations data-id="element-434" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-430" />
          <Route path="/reservation-detail" element={<ProtectedRoute data-id="element-436">
                <AppLayout data-id="element-437">
                  <PageTransitionWrapper data-id="element-438">
                    <ReservationDetail data-id="element-439" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-435" />
          <Route path="/invoices" element={<ProtectedRoute data-id="element-441">
                <AppLayout data-id="element-442">
                  <PageTransitionWrapper data-id="element-443">
                    <Invoices data-id="element-444" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-440" />
          <Route path="/invoice-detail/:id" element={<ProtectedRoute data-id="element-446">
                <AppLayout data-id="element-447">
                  <PageTransitionWrapper data-id="element-448">
                    <InvoiceDetail data-id="element-449" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-445" />
          <Route path="/invoice-form" element={<ProtectedRoute data-id="element-451">
                <AppLayout data-id="element-452">
                  <PageTransitionWrapper data-id="element-453">
                    <InvoiceForm data-id="element-454" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-450" />
          <Route path="/customers" element={<ProtectedRoute data-id="element-456">
                <AppLayout data-id="element-457">
                  <PageTransitionWrapper data-id="element-458">
                    <Customers data-id="element-459" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-455" />
          <Route path="/customer-detail/:id" element={<ProtectedRoute data-id="element-461">
                <AppLayout data-id="element-462">
                  <PageTransitionWrapper data-id="element-463">
                    <CustomerDetail data-id="element-464" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-460" />
          <Route path="/customer-form" element={<ProtectedRoute data-id="element-466">
                <AppLayout data-id="element-467">
                  <PageTransitionWrapper data-id="element-468">
                    <CustomerForm data-id="element-469" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-465" />
          <Route path="/sales" element={<ProtectedRoute data-id="element-471">
                <AppLayout data-id="element-472">
                  <PageTransitionWrapper data-id="element-473">
                    <Sales data-id="element-474" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-470" />
          <Route path="/sale-detail/:id" element={<ProtectedRoute data-id="element-476">
                <AppLayout data-id="element-477">
                  <PageTransitionWrapper data-id="element-478">
                    <SaleDetail data-id="element-479" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-475" />
          <Route path="/balance" element={<ProtectedRoute data-id="element-481">
                <AppLayout data-id="element-482">
                  <PageTransitionWrapper data-id="element-483">
                    <BalanceSummary data-id="element-484" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-480" />
          <Route path="/balance-activity" element={<ProtectedRoute data-id="element-486">
                <AppLayout data-id="element-487">
                  <PageTransitionWrapper data-id="element-488">
                    <BalanceActivity data-id="element-489" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-485" />
          <Route path="/balance-activity/:id" element={<ProtectedRoute data-id="element-491">
                <AppLayout data-id="element-492">
                  <PageTransitionWrapper data-id="element-493">
                    <BalanceActivityDetail data-id="element-494" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-490" />
          <Route path="/payouts" element={<ProtectedRoute data-id="element-496">
                <AppLayout data-id="element-497">
                  <PageTransitionWrapper data-id="element-498">
                    <Payouts data-id="element-499" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-495" />
          <Route path="/payouts/:id" element={<ProtectedRoute data-id="element-501">
                <AppLayout data-id="element-502">
                  <PageTransitionWrapper data-id="element-503">
                    <PayoutDetail data-id="element-504" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-500" />
          <Route path="/suppliers" element={<ProtectedRoute data-id="element-506">
                <AppLayout data-id="element-507">
                  <PageTransitionWrapper data-id="element-508">
                    <Suppliers data-id="element-509" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-505" />
          <Route path="/supplier-detail/:id" element={<ProtectedRoute data-id="element-511">
                <AppLayout data-id="element-512">
                  <PageTransitionWrapper data-id="element-513">
                    <SupplierDetail data-id="element-514" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-510" />
          <Route path="/supplier-form" element={<ProtectedRoute data-id="element-516">
                <AppLayout data-id="element-517">
                  <PageTransitionWrapper data-id="element-518">
                    <SupplierForm data-id="element-519" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-515" />
          <Route path="/supplier-bulk-upload" element={<ProtectedRoute data-id="element-521">
                <AppLayout data-id="element-522">
                  <PageTransitionWrapper data-id="element-523">
                    <BulkSupplierUpload data-id="element-524" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-520" />
          <Route path="/bill-bulk-upload" element={<ProtectedRoute data-id="element-526">
                <AppLayout data-id="element-527">
                  <PageTransitionWrapper data-id="element-528">
                    <BulkBillUpload data-id="element-529" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-525" />
          <Route path="/reconciliation" element={<ProtectedRoute data-id="element-531">
                <AppLayout data-id="element-532">
                  <PageTransitionWrapper data-id="element-533">
                    <Reconciliation data-id="element-534" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-530" />
          <Route path="/bills" element={<ProtectedRoute data-id="element-536">
                <AppLayout data-id="element-537">
                  <PageTransitionWrapper data-id="element-538">
                    <Bills data-id="element-539" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-535" />
          <Route path="/bill-detail/:id" element={<ProtectedRoute data-id="element-541">
                <AppLayout data-id="element-542">
                  <PageTransitionWrapper data-id="element-543">
                    <BillDetail data-id="element-544" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-540" />
          <Route path="/bill-form" element={<ProtectedRoute data-id="element-546">
                <AppLayout data-id="element-547">
                  <PageTransitionWrapper data-id="element-548">
                    <BillForm data-id="element-549" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-545" />
          <Route path="/bill-approval/:id" element={<ProtectedRoute data-id="element-551">
                <AppLayout data-id="element-552">
                  <PageTransitionWrapper data-id="element-553">
                    <BillApprovalWizard data-id="element-554" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-550" />
          <Route path="/bill-financing/:id" element={<ProtectedRoute data-id="element-556">
                <AppLayout data-id="element-557">
                  <PageTransitionWrapper data-id="element-558">
                    <BillFinancingWizard data-id="element-559" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-555" />
          <Route path="/disputes" element={<ProtectedRoute data-id="element-561">
                <AppLayout data-id="element-562">
                  <PageTransitionWrapper data-id="element-563">
                    <Disputes data-id="element-564" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-560" />
          <Route path="/dispute-detail/:id" element={<ProtectedRoute data-id="element-566">
                <AppLayout data-id="element-567">
                  <PageTransitionWrapper data-id="element-568">
                    <DisputeDetail data-id="element-569" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-565" />
          <Route path="/disputes/:id" element={<ProtectedRoute data-id="element-571">
                <AppLayout data-id="element-572">
                  <PageTransitionWrapper data-id="element-573">
                    <DisputeDetail data-id="element-574" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-570" />
          <Route path="/disputes/:id/challenge" element={<ProtectedRoute data-id="element-576">
                <ChallengeDispute data-id="element-577" />
              </ProtectedRoute>} data-id="element-575" />
          <Route path="/refunds" element={<ProtectedRoute data-id="element-579">
                <AppLayout data-id="element-580">
                  <PageTransitionWrapper data-id="element-581">
                    <Refunds data-id="element-582" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-578" />
          <Route path="/refund-detail/:id" element={<ProtectedRoute data-id="element-584">
                <AppLayout data-id="element-585">
                  <PageTransitionWrapper data-id="element-586">
                    <RefundDetail data-id="element-587" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-583" />
          <Route path="/custom-metrics" element={<ProtectedRoute data-id="element-589">
                <AppLayout data-id="element-590">
                  <PageTransitionWrapper data-id="element-591">
                    <CustomMetrics data-id="element-592" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-588" />
          <Route path="/nadi" element={<ProtectedRoute data-id="element-594">
                <AppLayout data-id="element-595">
                  <PageTransitionWrapper data-id="element-596">
                    <Nadi data-id="element-597" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-593" />
          <Route path="/reports" element={<ProtectedRoute data-id="element-599">
                <AppLayout data-id="element-600">
                  <PageTransitionWrapper data-id="element-601">
                    <Reports data-id="element-602" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-598" />
          <Route path="/card-monitoring" element={<ProtectedRoute data-id="element-604">
                <AppLayout data-id="element-605">
                  <PageTransitionWrapper data-id="element-606">
                    <CardMonitoringPrograms data-id="element-607" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-603" />
          <Route path="/card-monitoring/vamp" element={<ProtectedRoute data-id="element-609">
                <AppLayout data-id="element-610">
                  <PageTransitionWrapper data-id="element-611">
                    <VampDetail data-id="element-612" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-608" />
          <Route path="/payment-analytics" element={<ProtectedRoute data-id="element-614">
                <AppLayout data-id="element-615">
                  <PageTransitionWrapper data-id="element-616">
                    <PaymentAnalytics data-id="element-617" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-613" />
          <Route path="/activity-logs" element={<ProtectedRoute data-id="element-619">
                <AppLayout data-id="element-620">
                  <PageTransitionWrapper data-id="element-621">
                    <ActivityLogs data-id="element-622" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-618" />
          <Route path="/requests" element={<ProtectedRoute data-id="element-624">
                <AppLayout data-id="element-625">
                  <PageTransitionWrapper data-id="element-626">
                    <Requests data-id="element-627" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-623" />
          <Route path="/requests/:id" element={<ProtectedRoute data-id="element-629">
                <AppLayout data-id="element-630">
                  <PageTransitionWrapper data-id="element-631">
                    <RequestDetail data-id="element-632" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-628" />
        </Routes>
      </Suspense>
    </BrowserRouter>;
}